import { useState } from 'react';

export interface VmScheduleView {
  vmKey: string;
  /** ISO weekdays: 1 = Monday ... 7 = Sunday. */
  days: number[];
  startTime?: string;
  stopTime?: string;
  timezone: string;
}

const DAYS: Array<[number, string]> = [
  [1, 'Mon'],
  [2, 'Tue'],
  [3, 'Wed'],
  [4, 'Thu'],
  [5, 'Fri'],
  [6, 'Sat'],
  [7, 'Sun'],
];

function browserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

function timezones(current: string): string[] {
  const supported = (
    Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
  ).supportedValuesOf;
  const all = supported ? supported('timeZone') : [];
  return all.includes(current) ? all : [current, ...all];
}

export function describeSchedule(schedule: VmScheduleView): string {
  const days = DAYS.filter(([day]) => schedule.days.includes(day)).map(
    ([, label]) => label,
  );
  const everyDay = days.length === 7;
  const parts = [
    schedule.startTime ? `starts ${schedule.startTime}` : undefined,
    schedule.stopTime ? `stops ${schedule.stopTime}` : undefined,
  ].filter(Boolean);
  return `${everyDay ? 'Every day' : days.join(', ')}: ${parts.join(', ')} (${
    schedule.timezone
  })`;
}

/**
 * The autostart schedule of one VM: a summary with Edit and Clear, and a form for the days, the
 * start time, an optional stop time (stop deallocates the VM) and the timezone they are read in.
 */
export function VmScheduleEditor({
  vmLabel,
  vmKey,
  schedule,
  onSave,
  onClear,
}: {
  vmLabel: string;
  vmKey: string;
  schedule?: VmScheduleView;
  onSave: (input: Omit<VmScheduleView, 'vmKey'>) => Promise<void>;
  onClear: () => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [startTime, setStartTime] = useState('08:00');
  const [stopTime, setStopTime] = useState('');
  const [timezone, setTimezone] = useState(browserTimezone());

  function edit() {
    setDays(schedule?.days ?? [1, 2, 3, 4, 5]);
    setStartTime(schedule ? schedule.startTime ?? '' : '08:00');
    setStopTime(schedule?.stopTime ?? '');
    setTimezone(schedule?.timezone ?? browserTimezone());
    setError(null);
    setEditing(true);
  }

  async function run(action: () => Promise<void>, done?: () => void) {
    setBusy(true);
    setError(null);
    try {
      await action();
      done?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const toggle = (day: number) =>
    setDays(current =>
      current.includes(day)
        ? current.filter(d => d !== day)
        : [...current, day],
    );

  if (!editing) {
    return (
      <div
        data-testid={`environment-vm-${vmKey}-schedule`}
        style={{ marginLeft: 16, fontSize: '0.9em' }}
      >
        Autostart: {schedule ? describeSchedule(schedule) : 'not set'}{' '}
        <button
          type="button"
          disabled={busy}
          onClick={edit}
          style={{ marginRight: 4 }}
        >
          {schedule ? 'Edit schedule' : 'Set schedule'}
        </button>
        {schedule && (
          <button type="button" disabled={busy} onClick={() => run(onClear)}>
            Clear schedule
          </button>
        )}
        {error && <p>{error}</p>}
      </div>
    );
  }

  return (
    <form
      data-testid={`environment-vm-${vmKey}-schedule-form`}
      aria-label={`Autostart schedule for ${vmLabel}`}
      style={{ marginLeft: 16, fontSize: '0.9em' }}
      onSubmit={event => {
        event.preventDefault();
        run(
          () =>
            onSave({
              days: [...days].sort((a, b) => a - b),
              startTime: startTime || undefined,
              stopTime: stopTime || undefined,
              timezone,
            }),
          () => setEditing(false),
        );
      }}
    >
      <fieldset style={{ border: 0, padding: 0 }}>
        <legend>Days</legend>
        {DAYS.map(([day, label]) => (
          <label key={day} style={{ marginRight: 8 }}>
            <input
              type="checkbox"
              checked={days.includes(day)}
              onChange={() => toggle(day)}
            />{' '}
            {label}
          </label>
        ))}
      </fieldset>
      <label style={{ marginRight: 8 }}>
        Start time{' '}
        <input
          type="time"
          value={startTime}
          onChange={e => setStartTime(e.target.value)}
        />
      </label>
      <label style={{ marginRight: 8 }}>
        Stop time (optional){' '}
        <input
          type="time"
          value={stopTime}
          onChange={e => setStopTime(e.target.value)}
        />
      </label>
      <label>
        Timezone{' '}
        <select value={timezone} onChange={e => setTimezone(e.target.value)}>
          {timezones(timezone).map(zone => (
            <option key={zone} value={zone}>
              {zone}
            </option>
          ))}
        </select>
      </label>
      <p style={{ margin: '4px 0' }}>
        Stopping deallocates the VM. Use a stop time after the start time.
      </p>
      <button type="submit" disabled={busy} style={{ marginRight: 4 }}>
        Save schedule
      </button>
      <button type="button" disabled={busy} onClick={() => setEditing(false)}>
        Cancel
      </button>
      {error && <p>{error}</p>}
    </form>
  );
}
